import { useMemo, useRef, useState } from 'react';

import { useDisableBodyScroll } from '../../../../hooks/scroll';
import { useLocalization } from '../../../../providers/LocaleProvider';
import useSearchLock from '../../../AutocompleteInputV2/hooks/use-search-lock';
import { getInputLength } from '../../utils/search';
import { useHandlers } from './handlers';
import { useQuery } from './query';

const SEARCH_ACTION_PATH = '/search';

type UseInput = {
  isMobile: boolean;
  shopName?: string;
  withInputFilled: boolean;
  onBlur: () => void;
};

const usePlaceholder = (focused: boolean, shopName?: string) => {
  const { t } = useLocalization();

  if (focused) {
    return '';
  }

  return shopName
    ? `${shopName} ${t('h24_search_bar_shop_placeholder')}`
    : t('h24_search_bar_placeholder');
};

const useActionPath = () => {
  const { prefix } = useLocalization();

  return prefix(SEARCH_ACTION_PATH);
};

export const useInput = ({ isMobile, shopName, withInputFilled, onBlur }: UseInput) => {
  const [value, setValue] = useState('');
  const [isEditedByUser, setIsEditedByUser] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isSearchClearFocused, setSearchClearFocused] = useState(false);
  const { isLocked, lockSearch, unlockSearch, searchContainerRef, searchPlaceholderRef } =
    useSearchLock();
  const isClearButtonHidden = useMemo(() => getInputLength(value) === 0, [value]);
  const ref = useRef<HTMLInputElement>(null);
  const fakeRef = useRef<HTMLInputElement>(null);
  const placeholder = usePlaceholder(focused, shopName);
  const handlers = useHandlers({
    ref,
    placeholder,
    setValue,
    setIsEditedByUser,
    setFocused,
    setSearchClearFocused,
    unlockSearch,
    isLocked,
    onBlur,
  });

  useQuery(setValue, withInputFilled);
  useDisableBodyScroll(focused && isMobile, '[data-testid="suggestions-list"]');

  return {
    value,
    setValue,
    refs: {
      input: ref,
      fakeInput: fakeRef,
      container: searchContainerRef,
      placeholder: searchPlaceholderRef,
    },
    actionPath: useActionPath(),
    placeholder,
    flags: {
      isEditedByUser,
      focused,
      isSearchClearFocused,
      isClearButtonHidden,
      isLocked,
    },
    handlers: { ...handlers, lockSearch, unlockSearch },
  };
};
